// Today, strategy by strategy: which sections print, in what order, and which
// ticket on each one the ranking liked.
//
// Every strategy runs — the hidden ones too, because a hidden strategy still
// votes in the ranking and still goes on the paper trail — so something has to
// decide what actually reaches the screen and in which order. That decision is
// here rather than in `store.js` so it can be tested without a board, a
// profile or a browser: results in, sections out, and no imports at all.
//
// THE ORDER IS AN ARGUMENT, not a preference. Classic Four Ways is first
// because it is the app as shipped and the one section a reader has a habit
// about; moving it around by value would make the top of Today a different
// screen every morning. Everything after it is ranked by the only test the app
// applies across strategies — how much better your book pays than the market
// says the ticket is worth — so the further down you read, the less the
// ranking thinks of it. A strategy with nothing the ranking could measure is
// not scored last, it is scored not at all, so those sit at the bottom in the
// order they were run and stay put.

/**
 * Is `a` a better pick than `b`?
 *
 * Rank first, because `rankTickets` has already done this comparison with its
 * own tie-breaks and a second opinion here could disagree with the numbers
 * printed beside it. Value is the fallback for a pick that arrived without a
 * rank.
 */
function better(a, b) {
  if (!b) return true;
  if (Number.isFinite(a?.rank) && Number.isFinite(b.rank)) return a.rank < b.rank;
  return (a?.valueVsMarket ?? -Infinity) > (b.valueVsMarket ?? -Infinity);
}

/**
 * The sections Today prints.
 *
 * `results` is one entry per strategy — `{ strategy, tickets, candidates,
 * notes }`, exactly what `runStrategy` gives back — and `picks` is the ranked
 * list from `rankTickets`, over every strategy including the hidden ones.
 *
 * Answers [{ strategy, tickets, candidates, notes, best }], where every ticket
 * has gained a `pick` of `{ rank, valueVsMarket, chance }` or null, and `best`
 * is the section's own best pick or null.
 *
 * Only the three fields the screen prints are carried onto a ticket. The pick
 * also holds the multi and its legs, which are the very objects already on the
 * ticket — copying them across would put two of everything in the pad and
 * leave a reader of this code wondering which one is authoritative.
 */
export function sectionsFor(results, picks, { hidden = [], classicId } = {}) {
  const hideIds = new Set(hidden ?? []);

  // By `multi.id`. Every ticket on the board has its own — Classic's name the
  // day and the tier, the engine's name the day, the strategy and the seed —
  // so a pick always lands back on the ticket it was scored from. The first
  // entry for an id wins; `rankTickets` has already dropped the second copy of
  // a bet two strategies both printed, and the survivor is the better-priced
  // one.
  const byMulti = new Map();
  for (const p of picks ?? []) {
    const id = p?.multi?.id;
    if (id == null || byMulti.has(id)) continue;
    byMulti.set(id, {
      rank: p.rank ?? null,
      valueVsMarket: p.valueVsMarket ?? null,
      chance: p.chance ?? null,
    });
  }

  const sections = [];
  for (const result of results ?? []) {
    if (hideIds.has(result?.strategy?.id)) continue;
    let best = null;
    const tickets = (result?.tickets ?? []).map((t) => {
      const pick = byMulti.get(t?.multi?.id) ?? null;
      if (pick && better(pick, best)) best = pick;
      return { ...t, pick };
    });
    sections.push({
      strategy: result?.strategy ?? null,
      tickets,
      candidates: result?.candidates,
      notes: result?.notes,
      best,
    });
  }

  const isClassic = (s) => classicId != null && s.strategy?.id === classicId;
  const classic = sections.filter(isClassic);
  const ranked = sections.filter((s) => !isClassic(s) && s.best);
  // Sorted, not filtered out: a strategy the ranking could not measure still
  // printed tickets, and a section that vanished on a quiet day would read as
  // a bug. `sort` is stable, so these keep the order they were run in.
  const unranked = sections.filter((s) => !isClassic(s) && !s.best);
  ranked.sort((a, b) => (b.best.valueVsMarket ?? -Infinity) - (a.best.valueVsMarket ?? -Infinity));

  return [...classic, ...ranked, ...unranked];
}
